/**
 * Color Extraction Utility
 *
 * Extracts dominant colors from an image using ColorThief.
 * Used to suggest brand colors based on uploaded logo.
 */

import ColorThief from 'colorthief';
import type { ExtractedColors } from '../types/onboarding';

export type { ExtractedColors };

/**
 * Convert RGB array to hex string
 */
function rgbToHex(rgb: [number, number, number]): string {
  return '#' + rgb.map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Calculate color brightness (0-255)
 */
function getBrightness(rgb: [number, number, number]): number {
  return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
}

/**
 * Calculate color saturation (0-1)
 */
function getSaturation(rgb: [number, number, number]): number {
  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return 0;
  }

  return l > 0.5
    ? (max - min) / (2 - max - min)
    : (max - min) / (max + min);
}

/**
 * Sort colors by vibrancy (saturation * brightness balance)
 * We want colors that are visible and vibrant, not too dark or washed out
 */
function sortByVibrancy(colors: [number, number, number][]): [number, number, number][] {
  return colors.sort((a, b) => {
    const satA = getSaturation(a);
    const satB = getSaturation(b);
    const brightA = getBrightness(a);
    const brightB = getBrightness(b);

    // Score based on saturation and avoiding extreme brightness
    // Ideal brightness is around 100-180 (not too dark, not too light)
    const brightScoreA = 1 - Math.abs(brightA - 140) / 140;
    const brightScoreB = 1 - Math.abs(brightB - 140) / 140;

    const scoreA = satA * 0.7 + brightScoreA * 0.3;
    const scoreB = satB * 0.7 + brightScoreB * 0.3;

    return scoreB - scoreA;
  });
}

/**
 * Filter out colors that are too similar to each other
 */
function filterSimilarColors(colors: [number, number, number][], minDistance = 50): [number, number, number][] {
  const filtered: [number, number, number][] = [];

  for (const color of colors) {
    const isTooSimilar = filtered.some(existing => {
      const distance = Math.sqrt(
        Math.pow(color[0] - existing[0], 2) +
        Math.pow(color[1] - existing[1], 2) +
        Math.pow(color[2] - existing[2], 2)
      );
      return distance < minDistance;
    });

    if (!isTooSimilar) {
      filtered.push(color);
    }
  }

  return filtered;
}

/**
 * Filter out near-white and near-black colors
 */
function filterExtremeColors(colors: [number, number, number][]): [number, number, number][] {
  return colors.filter(color => {
    const brightness = getBrightness(color);
    // Filter out very dark (< 30) and very light (> 230) colors
    return brightness > 30 && brightness < 230;
  });
}

/**
 * Extract colors from an image URL
 *
 * @param imageUrl - URL of the image to analyze
 * @returns Promise with extracted primary, secondary, and palette colors
 */
export async function extractColorsFromImage(imageUrl: string): Promise<ExtractedColors | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';

    img.onload = () => {
      try {
        const colorThief = new ColorThief();

        // Get palette of 8 colors
        const rawPalette = colorThief.getPalette(img, 8) as [number, number, number][];

        if (!rawPalette || rawPalette.length < 2) {
          resolve(null);
          return;
        }

        // Filter and sort colors
        let processedColors = filterExtremeColors(rawPalette);
        processedColors = filterSimilarColors(processedColors);
        processedColors = sortByVibrancy(processedColors);

        // If we filtered too many, use original palette
        if (processedColors.length < 2) {
          processedColors = sortByVibrancy(rawPalette).slice(0, 4);
        }

        // Convert to hex
        const hexPalette = processedColors.map(rgbToHex);

        // Primary is the most vibrant color
        // Secondary is the second most vibrant (or a darker shade of primary if only one good color)
        const primary = hexPalette[0];
        const secondary = hexPalette[1] || darkenColor(primary, 20);

        resolve({
          primary,
          secondary,
          palette: hexPalette.slice(0, 4), // Return up to 4 colors
        });
      } catch (error) {
        console.error('Error extracting colors:', error);
        resolve(null);
      }
    };

    img.onerror = () => {
      console.error('Failed to load image for color extraction');
      resolve(null);
    };

    img.src = imageUrl;
  });
}

/**
 * Darken a hex color by a percentage
 */
function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max((num >> 16) - amt, 0);
  const G = Math.max((num >> 8 & 0x00FF) - amt, 0);
  const B = Math.max((num & 0x0000FF) - amt, 0);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}

/**
 * Check if a color is considered "vibrant" enough for a brand color
 */
export function isVibrantColor(hex: string): boolean {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  const saturation = getSaturation([r, g, b]);
  const brightness = getBrightness([r, g, b]);

  return saturation > 0.2 && brightness > 40 && brightness < 220;
}
